import { toUnixEpochTime } from "utils/date-format";
import type { RegisterDocumentRequest } from "components/create-audit-form/definitions";

/**
 * Register a scanned document with the system
 */
export const registerScanDocument = async (
  shortId: string | undefined,
  scanJobData: any,
  noticeId: string,
  teamName: string,
  registerDocumentCall: any
): Promise<string | null> => {
  if (!shortId || !scanJobData?.scanJob?.inputS3Key) {
    return null;
  }

  try {
    const registerDocumentRequest: RegisterDocumentRequest = {
      parentId: noticeId,
      gaiaObjectId: noticeId,
      teamName: teamName,
      fileName: scanJobData.scanJob.fileName,
      documentType: "Notice letters",
      securityLevel: "Normal",
      extentionType: scanJobData.scanJob.extensionType,
      s3Prefix: scanJobData.scanJob.inputS3Key,
      size: scanJobData.scanJob.size,
    };

    await registerDocumentCall(registerDocumentRequest).unwrap();
    return null;
  } catch (error: any) {
    console.error("Failed to register scan job document:", error);
    return "Failed to attach scanned file to the notice";
  }
};

/**
 * Update scan job status to "Review complete"
 */
export const updateScanStatus = async (
  shortId: string | undefined,
  noticeId: string,
  updateScanJobCall: any
): Promise<string | null> => {
  if (!shortId) {
    return null;
  }

  try {
    await updateScanJobCall({
      shortId: shortId,
      gaiaObjectId: noticeId,
      status: "Review complete",
    }).unwrap();
    return null;
  } catch (error: any) {
    console.error("Failed to update scan job:", error);
    return "Failed to mark scan job as complete";
  }
};

/**
 * Create an assessment from localStorage data
 */
export const createAssessmentFromData = async (
  noticeId: string,
  assessmentStorageKey: string,
  shortId: string | undefined,
  isAssessmentSimplified: boolean,
  createAssessment: any
): Promise<{ success: boolean; error: string | null }> => {
  const assessmentDataStr = localStorage.getItem(assessmentStorageKey);
  if (!assessmentDataStr || !shortId || isAssessmentSimplified) {
    return { success: false, error: null };
  }

  try {
    const { type, dateReceived, notes, amounts, parsedBreakdowns } =
      JSON.parse(assessmentDataStr);

    const assessmentRequest = {
      gaiaObjectId: noticeId,
      type: type || "Preliminary",
      dateReceived: dateReceived ? toUnixEpochTime(dateReceived) : Date.now(),
      totalTaxAmount: amounts.totalTaxAmount || "0",
      totalPenaltyAmount: amounts.totalPenaltyAmount || "0",
      totalInterestAmount: amounts.totalInterestAmount || "0",
      totalOtherAmount: amounts.totalOtherAmount || "0",
      notes: notes,
      taxAreaBreakdown: [],
      taxYearBreakdown: parsedBreakdowns || [],
    };

    await createAssessment(assessmentRequest).unwrap();
    return { success: true, error: null };
  } catch (error: any) {
    console.error("Error creating assessment:", error);
    return {
      success: false,
      error: error?.message || "Failed to create assessment",
    };
  }
};

/**
 * Generate notification configuration based on operation results
 */
export const getNotificationConfig = (
  noticeTitle: string,
  assessmentResult: { success: boolean; error: string | null },
  warnings: string[]
) => {
  const hasWarnings = warnings.length > 0;
  const warningsList = warnings.map((w) => `• ${w}`).join("\n");

  // All successful
  if (assessmentResult.success && !hasWarnings) {
    return {
      type: "success" as const,
      header: "Notice and Assessment created successfully",
      content: `Notice "${noticeTitle}" and associated assessment have been created successfully.`,
    };
  }

  // Assessment and notice created, but with warnings
  if (assessmentResult.success && hasWarnings) {
    return {
      type: "warning" as const,
      header: "Notice and Assessment created with warnings",
      content: `Notice "${noticeTitle}" and assessment were created, but some operations failed:\n${warningsList}`,
    };
  }

  // Assessment failed, with or without warnings
  if (assessmentResult.error) {
    const messages = [`• Assessment creation: ${assessmentResult.error}`];
    if (hasWarnings) {
      messages.push(...warnings.map((w) => `• ${w}`));
    }
    return {
      type: "warning" as const,
      header: "Notice created with issues",
      content: `Notice "${noticeTitle}" was created, but encountered the following issues:\n${messages.join(
        "\n"
      )}`,
    };
  }

  // Notice created, possibly with warnings
  if (hasWarnings) {
    return {
      type: "warning" as const,
      header: "Notice created with warnings",
      content: `Notice "${noticeTitle}" was created, but some operations failed:\n${warningsList}`,
    };
  }

  // Only notice created successfully
  return {
    type: "success" as const,
    header: "Notice created successfully",
    content: `Notice "${noticeTitle}" has been created successfully.`,
  };
};
